// frontend/src/pages/SociosPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

export default function SociosPage() {
  const { usuario } = useAuth();
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // busy por ID de sociedade
  const [errorMsg, setErrorMsg] = useState('');

  const fetchSocios = useCallback(async () => {
    if (!usuario?.uid) return;
    setLoading(true);
    setErrorMsg('');

    try {
      // todos os docs de sociedade ACEITOS onde sou criador OU convidado
      const q1 = query(
        collection(db, 'socios'),
        where('status', '==', 'accepted'),
        where('criador', '==', usuario.uid)
      );
      const q2 = query(
        collection(db, 'socios'),
        where('status', '==', 'accepted'),
        where('convidado', '==', usuario.uid)
      );

      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      const allDocs = [...snap1.docs, ...snap2.docs];

      const itens = await Promise.all(
        allDocs.map(async (docSnap) => {
          const data = docSnap.data();
          const socioId =
            data.criador === usuario.uid ? data.convidado : data.criador;

          // pega o usuário diretamente pelo id
          const userSnap = await getDoc(doc(db, 'users', socioId));
          const userData = userSnap.exists() ? userSnap.data() : {};

          // imóveis do sócio compartilhados com sócios
          const imoveisSnap = await getDocs(
            query(
              collection(db, 'imoveis'),
              where('criadoPor.uid', '==', socioId),
              where('compartilharComSocios', '==', true)
            )
          );

          const imoveis = imoveisSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));

          return {
            id: docSnap.id, // id do doc em 'socios'
            socioId, // uid do sócio
            nome: userData?.nome || 'Sem nome',
            email: userData?.email || 'Sem e-mail',
            foto: userData?.fotoPerfil?.url || userData?.fotoPerfil || null,
            imoveis,
          };
        })
      );

      // dedup por socioId (se houver entradas duplicadas)
      const dedup = [];
      const seen = new Set();
      for (const item of itens) {
        if (!seen.has(item.socioId)) {
          seen.add(item.socioId);
          dedup.push(item);
        }
      }

      setSocios(dedup);
    } catch (error) {
      console.error('Erro ao buscar sócios:', error);
      setErrorMsg('Não foi possível carregar seus sócios agora.');
    } finally {
      setLoading(false);
    }
  }, [usuario?.uid]);

  useEffect(() => {
    fetchSocios();
  }, [fetchSocios]);

  const encerrar = async (id) => {
    const ok = window.confirm(
      'Tem certeza que deseja encerrar esta sociedade?'
    );
    if (!ok) return;

    try {
      setBusy((s) => ({ ...s, [id]: true }));
      await updateDoc(doc(db, 'socios', id), {
        status: 'ended',
        endedAt: serverTimestamp(),
        endedBy: usuario?.uid || null,
      });
      setSocios((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      console.error('Erro ao encerrar sociedade:', e);
      alert('Não foi possível encerrar a sociedade. Tente novamente.');
    } finally {
      setBusy((s) => ({ ...s, [id]: false }));
    }
  };

  const empty = !loading && socios.length === 0;

  const totalImoveis = useMemo(
    () => socios.reduce((acc, s) => acc + (s.imoveis?.length || 0), 0),
    [socios]
  );

  if (!usuario?.uid) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
        <p>Carregando dados do usuário...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold">Meus Sócios</h1>
          {!loading && (
            <p className="text-sm text-gray-500">
              {socios.length} sócio(s) ativos • {totalImoveis} imóvel(is)
              compartilhados no total
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchSocios}
            className="px-3 py-2 text-sm bg-white border rounded hover:bg-gray-50"
          >
            Recarregar
          </button>
        </div>
      </div>

      {errorMsg && (
        <div className="p-3 mb-4 rounded bg-red-50 text-red-700 text-sm border border-red-200">
          {errorMsg}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-32 animate-pulse bg-gray-100 rounded border"
            />
          ))}
        </div>
      ) : empty ? (
        <div className="p-6 bg-white border rounded text-center text-gray-600">
          <p>Você ainda não possui sociedades ativas.</p>
          <p className="text-sm text-gray-500 mt-1">
            Envie convites a corretores para iniciar uma sociedade.
          </p>
        </div>
      ) : (
        <ul className="space-y-6">
          {socios.map((socio) => (
            <li
              key={socio.id}
              className="bg-white shadow-sm border p-4 rounded-lg"
            >
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={socio.foto || '/placeholder-user.jpg'}
                    alt=""
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    loading="lazy"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{socio.nome}</p>
                    <p className="text-sm text-gray-600 truncate">
                      {socio.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Link
                    to={`/perfil/${socio.socioId}`}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Ver perfil
                  </Link>
                  <button
                    onClick={() => encerrar(socio.id)}
                    disabled={!!busy[socio.id]}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60"
                  >
                    {busy[socio.id] ? 'Encerrando...' : 'Encerrar sociedade'}
                  </button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Imóveis compartilhados com você:
                </p>
                {socio.imoveis.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Nenhum imóvel compartilhado.
                  </p>
                ) : (
                  <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {socio.imoveis.map((imovel) => (
                      <li
                        key={imovel.id}
                        className="border rounded p-2 text-sm bg-gray-50"
                      >
                        <p className="font-semibold truncate">
                          {imovel.titulo || 'Imóvel sem título'}
                        </p>
                        <p className="text-gray-600 truncate">
                          {imovel.endereco || 'Endereço não informado'}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
